/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import {
  AuthError,
  BrowserAuthOptions,
  Configuration,
  EndSessionRequest,
  InteractionRequiredAuthError,
  LogLevel,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
const msalConfig: Configuration = {
  auth: {
    clientId: '',
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: any) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};

let authConfiguration: Configuration = msalConfig;

export const getMsalInstance = async () => {
  let msalInstance: PublicClientApplication = new PublicClientApplication(
    authConfiguration
  );
  try {
    const updatedAuth: BrowserAuthOptions = await fetchAuthConfiguration();
    const auth = { ...msalConfig.auth, ...updatedAuth };
    authConfiguration = { ...msalConfig, auth };
    msalInstance = new PublicClientApplication(authConfiguration);
  } catch (e) {
    console.error((e as Error).message);
  }
  return msalInstance;
};
const fetchAuthConfiguration = async () => {
  const response = await fetch(`api/clientconfiguration/`);
  const responseAsJSON = await response.json();
  const data: BrowserAuthOptions = responseAsJSON;
  return data;
};

export function useAuth() {
  const { instance: msalInstance, accounts } = useMsal();

  const readRequest: SilentRequest = {
    scopes: [`api://${authConfiguration.auth.clientId}/api.read`],
    account: accounts[0],
  };
  const modifyRequest: SilentRequest = {
    scopes: [`api://${authConfiguration.auth.clientId}/api.modify`],
    account: accounts[0],
  };
  const acquireAccessToken = async (request: SilentRequest) => {
    let currentToken = '';
    try {
      const { accessToken } = await msalInstance.acquireTokenSilent(request);
      currentToken = accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        msalInstance.acquireTokenRedirect(request);
      }
      console.log((error as Error).message);
    }
    return currentToken;
  };
  const acquireReadToken = async () => {
    const accessToken = await acquireAccessToken(readRequest);
    return accessToken;
  };
  const acquireModifyToken = async () => {
    const accessToken = await acquireAccessToken(modifyRequest);
    return accessToken;
  };
  const logout = async () => {
    const logoutRequest: EndSessionRequest = {
      account: accounts[0],
    };
    msalInstance.logoutRedirect(logoutRequest);
  };
  return { acquireModifyToken, acquireReadToken, logout };
}
